import React, { useEffect, useState } from "react";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";

import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ui/confirm";


function Money({ minor }) {
  return <span className="tabular-nums">{((minor || 0) / 100).toFixed(2)}</span>;
}


export default function ProviderAdminPanel() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const isOwner = user?.role === "owner" || user?.role === "super_admin";
  const [providers, setProviders] = useState([]);
  const [recon, setRecon] = useState({ providers: {}, callbacks: {} });
  const [preflight, setPreflight] = useState(null);
  const [configAudit, setConfigAudit] = useState([]);
  const [busy, setBusy] = useState("");
  // Per-source errors — each diagnostic loads independently so one failing
  // endpoint doesn't blank out the others (allSettled, not all).
  const [errors, setErrors] = useState({});

  const load = async () => {
    const [pRes, rRes, pfRes, aRes] = await Promise.allSettled([
      api.get("/casino/admin/providers"),
      api.get("/casino/admin/provider-reconciliation"),
      api.get("/casino/admin/provider-preflight"),
      api.get("/casino/admin/provider-config-audit?limit=100"),
    ]);
    const nextErrors = {};
    if (pRes.status === "fulfilled") setProviders(pRes.value.data.providers || []);
    else nextErrors.providers = apiError(pRes.reason?.response?.data?.detail) || "Failed to load providers";
    if (rRes.status === "fulfilled") setRecon(rRes.value.data || { providers: {}, callbacks: {} });
    else nextErrors.recon = apiError(rRes.reason?.response?.data?.detail) || "Failed to load reconciliation";
    if (pfRes.status === "fulfilled") setPreflight(pfRes.value.data || null);
    else nextErrors.preflight = apiError(pfRes.reason?.response?.data?.detail) || "Failed to load wallet preflight";
    if (aRes.status === "fulfilled") setConfigAudit(Array.isArray(aRes.value.data) ? aRes.value.data : []);
    else nextErrors.audit = apiError(aRes.reason?.response?.data?.detail) || "Failed to load config audit";
    setErrors(nextErrors);
  };

  useEffect(() => { load(); }, []);

  const patch = async (slug, changes) => {
    setBusy(`patch:${slug}`);
    try {
      await api.patch(`/casino/admin/providers/${slug}`, changes);
      toast.success(`${slug} settings updated`);
      await load();
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const sync = async (slug) => {
    setBusy(`sync:${slug}`);
    try {
      const { data } = await api.post(`/casino/admin/providers/${slug}/sync`);
      toast.success(`Synced ${data.games_synced} game(s) from ${slug}`);
      await load();
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="provider-admin-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-black">Casino Providers</h2>
          <p className="mt-1 max-w-3xl text-sm text-[#a1a1aa]">
            Kerma and JackTop stay fail-closed until their official operator protocols and credentials are certified.
            Disabling a provider stops new launches but does not block settlement callbacks for existing rounds.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full border-[#27272A]">
          <Icon name="refresh" size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {errors.preflight && (
        <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/5 px-4 py-3 text-sm text-[#EF4444]" data-testid="provider-preflight-error">
          Wallet preflight unavailable — {errors.preflight}
        </div>
      )}
      {preflight?.database && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          preflight.database.supported
            ? "border-[#00D632]/40 bg-[#00D632]/5 text-[#d8ffe1]"
            : "border-[#EF4444]/40 bg-[#EF4444]/5 text-[#EF4444]"
        }`} data-testid="provider-db-preflight">
          <b>Provider wallet DB preflight:</b>{" "}
          {preflight.database.supported
            ? `transaction-capable (${preflight.database.topology})`
            : `NOT transaction-capable (${preflight.database.topology || "unknown"})`}
          {!preflight.database.supported && (
            <span> — do not certify or launch a money provider on this database topology.</span>
          )}
        </div>
      )}

      {errors.providers && (
        <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/5 px-4 py-3 text-sm text-[#EF4444]" data-testid="provider-list-error">
          Providers unavailable — {errors.providers}
        </div>
      )}
      {(errors.recon || errors.audit) && (
        <div className="rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/5 px-4 py-3 text-xs text-[#EAB308]" data-testid="provider-aux-error">
          {errors.recon && <div>Reconciliation data unavailable — {errors.recon}</div>}
          {errors.audit && <div>Config-change history unavailable — {errors.audit}</div>}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-3">
        {providers.map((provider) => {
          const callbacks = recon.callbacks?.[provider.slug] || {};
          const wallets = recon.providers?.[provider.slug] || {};
          const providerAudit = configAudit.filter((row) => row.provider === provider.slug);
          const lastChange = providerAudit[0] || null;
          const isBusy = busy.endsWith(provider.slug);
          const smoke = (preflight?.providers || []).find((p) => p.provider === provider.slug)?.launch_smoke;
          return (
            <div key={provider.slug} className="rounded-2xl border border-[#27272A] bg-[#141417] p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-black">{provider.name}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-[#71717a]">{provider.slug}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                  provider.ready ? "bg-[#00D632]/10 text-[#00D632]" : "bg-white/5 text-[#00D632]"
                }`}>
                  {provider.ready ? "Protocol ready" : "Approval pending"}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Credentials</p>
                  <p className="mt-1 font-bold text-white">{provider.configured ? "Configured" : "Missing"}</p>
                  <p className="mt-1 text-[10px] text-[#71717a]">
                    {provider.active_session_count || 0} active session{provider.active_session_count === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Games</p>
                  <p className="mt-1 font-bold text-white">{provider.game_count || 0}</p>
                  <p className={`mt-1 text-[10px] ${
                    provider.last_catalog_sync_status === "failed"
                      ? "text-[#EF4444]"
                      : provider.last_catalog_sync_status === "success"
                        ? "text-[#00D632]"
                        : "text-[#71717a]"
                  }`}>
                    {provider.last_catalog_sync_status === "failed"
                      ? `sync failed · ${provider.last_catalog_sync_error_code || "unknown"}`
                      : provider.last_catalog_sync_status === "success"
                        ? `synced ${provider.last_catalog_sync_count ?? 0}${provider.last_catalog_sync_authoritative ? " · full" : " · partial-safe"}`
                        : "not synced yet"}
                  </p>
                </div>
                <div className="rounded-xl bg-black/20 p-3" data-testid={`provider-launch-smoke-${provider.slug}`}>
                  <p className="text-[#71717a]">Launches</p>
                  {smoke?.attempted ? (
                    <>
                      <p className={`mt-1 font-bold ${smoke.ok ? "text-[#00D632]" : "text-[#EF4444]"}`}>
                        {smoke.ok ? "Working" : "FAILING"}
                      </p>
                      {!smoke.ok && (
                        <p className="mt-1 text-[10px] text-[#EF4444]">
                          {smoke.code || "error"}{smoke.detail ? ` · ${smoke.detail}` : ""}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 font-bold text-[#71717a]">{smoke?.detail || "not probed"}</p>
                  )}
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Environment</p>
                  <p className="mt-1 font-bold capitalize text-white">{provider.environment || "sandbox"}</p>
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Certification</p>
                  <p className={`mt-1 font-bold ${provider.certified ? "text-[#00D632]" : "text-[#00D632]"}`}>
                    {provider.certified ? "Owner certified" : "Not certified"}
                  </p>
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Callbacks</p>
                  <p className="mt-1 font-bold text-white">{callbacks.success || 0} ok / {(callbacks.rejected || 0) + (callbacks.error || 0)} issue</p>
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Territory rules</p>
                  <p className="mt-1 font-bold text-white">
                    {(provider.allowed_countries || []).length} countries · {(provider.blocked_states || []).length} blocked states
                  </p>
                </div>
                <div className="rounded-xl bg-black/20 p-3">
                  <p className="text-[#71717a]">Eligibility</p>
                  <p className="mt-1 font-bold text-white">
                    {provider.min_age || 18}+ · {provider.require_kyc ? "KYC required" : "KYC optional"}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={isBusy || (!provider.ready && !provider.enabled)}
                  onClick={() => patch(provider.slug, { enabled: !provider.enabled })}
                  className={provider.enabled
                    ? "rounded-full bg-[#EF4444] text-white hover:bg-[#EF4444]"
                    : "rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]"}
                >
                  {isBusy && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                  {provider.enabled ? <Icon name="shield-alert" size={14} className="mr-2" /> : <Icon name="shield-check" size={14} className="mr-2" />}
                  {provider.enabled ? "Disable" : "Enable"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy || !provider.ready}
                  onClick={() => sync(provider.slug)}
                  className="rounded-full border-[#27272A]"
                >
                  Sync catalog
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => patch(provider.slug, { maintenance: !provider.maintenance })}
                  className="rounded-full border-[#27272A]"
                >
                  {provider.maintenance ? "End maintenance" : "Maintenance"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => patch(provider.slug, { min_age: (provider.min_age || 18) === 21 ? 18 : 21 })}
                  className="rounded-full border-[#27272A]"
                >
                  Age {(provider.min_age || 18) === 21 ? "18+" : "21+"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => patch(provider.slug, { require_kyc: !provider.require_kyc })}
                  className="rounded-full border-[#27272A]"
                >
                  {provider.require_kyc ? "Make KYC optional" : "Require KYC"}
                </Button>
                {isOwner && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isBusy || !provider.ready}
                    onClick={async () => {
                      if (provider.certified || (await confirm({
                        title: "Mark provider certified",
                        body: "Mark this provider certified only after its official sandbox/operator certification has passed.",
                        confirmText: "Mark certified",
                      }))) {
                        patch(provider.slug, { certified: !provider.certified });
                      }
                    }}
                    className="rounded-full border-[#27272A]"
                  >
                    {provider.certified ? "Revoke certification" : "Mark certified"}
                  </Button>
                )}
                {isOwner && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isBusy || !provider.ready || ((provider.environment || "sandbox") === "sandbox" && !provider.certified)}
                    onClick={() => patch(provider.slug, {
                      environment: (provider.environment || "sandbox") === "production" ? "sandbox" : "production",
                    })}
                    className="rounded-full border-[#27272A]"
                  >
                    {(provider.environment || "sandbox") === "production" ? "Return to sandbox" : "Promote production"}
                  </Button>
                )}
              </div>

              {lastChange && (
                <div className="mt-4 border-t border-[#27272A] pt-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717a]">
                    Last config change
                  </p>
                  <p className="mt-1 text-xs text-[#d4d4d8]">
                    {lastChange.actor || "unknown"} · {(lastChange.changed_fields || []).join(", ") || "configuration"}
                  </p>
                  <p className="mt-1 text-[10px] text-[#71717a]">
                    {lastChange.created_at ? new Date(lastChange.created_at).toLocaleString() : "unknown time"} · {providerAudit.length} audit record{providerAudit.length === 1 ? "" : "s"}
                  </p>
                </div>
              )}

              {Object.keys(wallets).length > 0 && (
                <div className="mt-4 border-t border-[#27272A] pt-4">
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#71717a]">Reconciliation</p>
                  <div className="space-y-2">
                    {Object.entries(wallets).map(([wallet, stats]) => (
                      <div key={wallet} className="flex items-center justify-between text-xs">
                        <span className="capitalize text-[#a1a1aa]">{wallet} · {stats.transaction_count} tx</span>
                        <span className={stats.house_net_minor >= 0 ? "font-bold text-[#00D632]" : "font-bold text-[#EF4444]"}>
                          House <Money minor={stats.house_net_minor} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
